/** Shipclues employee sidebar — matches apps.navigation.config */
export const EMPLOYEE_PANEL_PERMISSION_MODULES = [
  {
    label: "Shipclues — employee panel",
    key: "employee_panel",
    items: [
    
      {
        label: "Dashboard",
        key: "dashboard",
        allowCreate: false,
        allowUpdate: false,
      },
    
      {
        label: "Orders",
        key: "orders",
        allowCreate: true,
        allowUpdate: false,
      },
      {
        label: "Order Report",
        key: "order_report",
        allowCreate: false,
        allowUpdate: false,
      },
      {
        label: "Seller",
        key: "seller",
        allowCreate: false,
        allowUpdate: false,
      },
      {
        label: "seller login",
        key: "seller_login",
        allowCreate: false,
        allowUpdate: false,
      },
      {
        label: "Shipments",
        key: "shipments",
        allowCreate: false,
        allowUpdate: false,
      },
      {
        label: "Dispute Records",
        key: "dispute_records",
        allowCreate: true,
        allowUpdate: true,
      },
      {
        label: "Team & Employee",
        key: "admin_user",
        allowCreate: false,
        allowUpdate: false,
      },
    
    ],
  },
];

/** Resolve API permission row → catalogue item key */
export function resolveEmployeePanelPermissionKey(permissionKey = "", moduleCode = "") {
  const raw = String(permissionKey || moduleCode || "")
    .toLowerCase()
    .trim();
  if (!raw) return null;

  const aliases = {
    "cod.remittance": "cod_remittance",
    "cod_remittance": "cod_remittance",
    "seller-login": "seller_login",
    "seller_login": "seller_login",
    "admin_user.employee_user": "admin_user",
    "admin_user.admin_user": "admin_user",
    team: "admin_user",
    employee: "admin_user",
  };

  if (aliases[raw]) return aliases[raw];

  const flat = raw.includes(".") ? raw.split(".").pop() : raw;
  if (aliases[flat]) return aliases[flat];

  for (const mod of EMPLOYEE_PANEL_PERMISSION_MODULES) {
    for (const item of mod.items) {
      if (item.key === raw || item.key === flat) return item.key;
      if (raw === `employee_panel.${item.key}`) return item.key;
    }
  }

  return null;
}

/** API `designation` values for POST /admin-employee/employees/ */
export const EMPLOYEE_DESIGNATION_BY_ROLE = {
  hod: "hod",
  manager: "manager",
  team_member: "employee",
};

/** Map profile/roster designation strings → internal employment_role */
export function employmentRoleFromDesignation(designation) {
  const raw = String(designation || "").toLowerCase().trim();
  if (raw === "hod" || raw === "head_of_department") return "hod";
  if (raw === "manager") return "manager";
  if (
    raw === "employee" ||
    raw === "staff" ||
    raw === "team_member" ||
    raw === "team member"
  ) {
    return "team_member";
  }
  return null;
}

/** Resolve roster/API row → hod | manager | team_member (flags & designation before generic API role) */
export function resolveEmploymentRoleFromRoster(raw) {
  if (!raw || typeof raw !== "object") return "team_member";

  if (raw.is_hod || raw.is_head_of_department) return "hod";
  if (raw.is_manager) return "manager";

  const fromDesignation = employmentRoleFromDesignation(raw.designation);
  if (fromDesignation) return fromDesignation;

  const fromApi = String(raw.employment_role || "").toLowerCase().trim();
  if (fromApi === "hod") return "hod";
  if (fromApi === "manager") return "manager";
  if (
    fromApi === "employee" ||
    fromApi === "staff" ||
    fromApi === "team_member" ||
    fromApi === "team member"
  ) {
    return "team_member";
  }
  if (fromApi) return fromApi;
  return "team_member";
}

/** Normalize any designation input to API values: hod | manager | employee | custom text */
export function designationForApi(designation, formKind = "team_member") {
  const original = String(designation || "").trim();
  const raw = original.toLowerCase();
  if (raw === "hod" || raw === "manager") return raw;
  if (
    raw === "employee" ||
    raw === "staff" ||
    raw === "team_member" ||
    raw === "team member"
  ) {
    return "employee";
  }
  if (original) return original;
  return EMPLOYEE_DESIGNATION_BY_ROLE[formKind] || "employee";
}

export function defaultDesignationForFormKind(formKind) {
  return EMPLOYEE_DESIGNATION_BY_ROLE[formKind] || "employee";
}

/** Fallback roster row when logged-in HOD/manager is not yet in fetched list */
export function profileRosterRowFromAuth(authUser) {
  if (!authUser?.email) return null;

  const role =
    employmentRoleFromDesignation(authUser.designation) ||
    (authUser.is_hod || authUser.is_head_of_department ? "hod" : null) ||
    (authUser.is_manager ? "manager" : null) ||
    (authUser.employment_role === "hod" ||
    authUser.employment_role === "manager" ||
    authUser.employment_role === "team_member"
      ? authUser.employment_role
      : null);

  if (role !== "hod" && role !== "manager") return null;

  const first = authUser.first_name || "";
  const last = authUser.last_name || "";
  const name =
    authUser.full_name ||
    `${first} ${last}`.trim() ||
    authUser.userName ||
    authUser.email;

  return {
    id: authUser.id ?? authUser.user_id,
    first_name: first,
    last_name: last,
    name,
    email: authUser.email,
    mobile: String(authUser.phone_number || authUser.mobile || "").replace(
      /\D/g,
      ""
    ),
    department: authUser.department || "",
    designation: designationForApi(authUser.designation, role),
    employment_role: role,
    is_hod: role === "hod",
    is_manager: role === "manager",
    hod_id:
      role === "manager"
        ? authUser.hod_id ?? authUser.reports_to_hod_id ?? null
        : null,
    hod_name: authUser.hod_name || authUser.head_of_department_name || "—",
    reports_to_manager_id: null,
    manager_name: "—",
    is_active: authUser.is_active !== false,
    status: authUser.is_active === false ? "Blocked" : "Allowed",
    role_ids: Array.isArray(authUser.role_ids) ? authUser.role_ids : [],
    permission_ids: Array.isArray(authUser.permission_ids)
      ? authUser.permission_ids
      : [],
    assigned_sellers: [],
  };
}

/** Enrich auth user from admin-employee profile (designation → role flags) */
export function enrichAuthUserFromProfile(user) {
  if (!user || typeof user !== "object") return user;

  const employment_role =
    employmentRoleFromDesignation(user.designation) ||
    user.employment_role ||
    (user.is_hod || user.is_head_of_department
      ? "hod"
      : user.is_manager
        ? "manager"
        : null);

  return {
    ...user,
    ...(employment_role ? { employment_role } : {}),
    is_hod: Boolean(
      user.is_hod ||
        user.is_head_of_department ||
        employment_role === "hod" ||
        String(user.designation || "").toLowerCase() === "hod"
    ),
    is_manager: Boolean(
      user.is_manager ||
        employment_role === "manager" ||
        String(user.designation || "").toLowerCase() === "manager"
    ),
    designation: user.designation
      ? designationForApi(user.designation, employment_role || "team_member")
      : user.designation,
  };
}

/** Stub IDs for HOD full access until backend provides real permission IDs */
export const HOD_STUB_PERMISSION_IDS = [101, 102, 103, 104, 105, 106, 107, 108];

/** Managers: broad module access (creates employees under HOD) */
export const MANAGER_STUB_PERMISSION_IDS = [101, 102, 103, 104, 105, 106];

export const DEPARTMENT_OPTIONS = [
  { value: "__none__", label: "None" },
  { value: "sales", label: "Sales" },
  { value: "operations", label: "Operations" },
  { value: "tech", label: "Tech" },
  { value: "finance", label: "Finance" },
];

/** API `designation` field — POST/PATCH /admin-employee/employees/ */
export const DESIGNATION_OPTIONS = [
  { value: "hod", label: "Head of Department (HOD)" },
  { value: "manager", label: "Manager" },
  { value: "employee", label: "Employee" },
];

export function designationOptionsForFormKind(formKind) {
  if (formKind === "hod") {
    return DESIGNATION_OPTIONS.filter((d) => d.value === "hod");
  }
  if (formKind === "manager") {
    return DESIGNATION_OPTIONS.filter((d) => d.value === "manager");
  }
  return DESIGNATION_OPTIONS.filter((d) => d.value === "employee");
}

export function departmentValueForApi(deptOption) {
  if (!deptOption) return null;
  const v =
    typeof deptOption === "object" && deptOption?.value != null
      ? deptOption.value
      : deptOption;
  if (v === "__none__" || v === "") return null;
  return v;
}

export function departmentOptionFromApi(dept) {
  if (dept == null || dept === "") {
    return DEPARTMENT_OPTIONS.find((d) => d.value === "__none__") || null;
  }
  return DEPARTMENT_OPTIONS.find((d) => d.value === dept) || null;
}

export function designationOptionFromApi(designation, formKind) {
  const raw = String(designation || "").toLowerCase();
  const value =
    raw === "hod" || raw === "manager"
      ? raw
      : raw === "employee" || raw === "staff" || raw === "team_member"
        ? "employee"
        : EMPLOYEE_DESIGNATION_BY_ROLE[formKind] || "employee";
  return (
    DESIGNATION_OPTIONS.find((d) => d.value === value) ||
    designationOptionsForFormKind(formKind)[0] ||
    null
  );
}

/** role_ids sent to API */
export const ROLE_OPTIONS = [
  { value: 1, label: "Tech" },
  { value: 2, label: "Operations" },
  { value: 3, label: "Sales" },
  { value: 4, label: "Finance" },
];

/** Position in department (hierarchy) */
export const REPORTING_ROLE_OPTIONS = [
  { value: "hod", label: "Head of department (HOD)" },
  { value: "manager", label: "Manager — reports to HOD, creates employees" },
  { value: "team_member", label: "Team member — limited modules" },
];

/** Sidebar modules → numeric permission_ids for API (starts at 201) */
let nextModuleId = 201;
export const MODULE_PERMISSION_OPTIONS = EMPLOYEE_PANEL_PERMISSION_MODULES.flatMap(
  (mod) =>
    mod.items.map((item) => ({
      value: nextModuleId++,
      label: `${mod.label} › ${item.label}`,
      permission_key: item.key,
    }))
);

/** For accordion UI: group → items with numeric permission_id (same IDs as MODULE_PERMISSION_OPTIONS) */
const permissionOptionByKey = new Map(
  MODULE_PERMISSION_OPTIONS.map((o) => [o.permission_key, o])
);

export const MODULE_PERMISSION_GROUPS = EMPLOYEE_PANEL_PERMISSION_MODULES.map(
  (mod) => ({
    key: mod.key,
    label: mod.label,
    items: mod.items
      .map((item) => {
        const opt = permissionOptionByKey.get(item.key);
        if (!opt) return null;
        return {
          permission_key: item.key,
          label: item.label,
          permissionId: opt.value,
        };
      })
      .filter(Boolean),
  })
).filter((g) => g.items.length > 0);

export const NAVY = "#263f97";
export const NAVY_HOVER = "#1e327a";

export const ROSTER_TABLE_BTN =
  "inline-flex items-center justify-center gap-1.5 border border-[#263f97] text-[#263f97] hover:bg-[#263f97]/[0.06] rounded-xl px-3 py-1.5 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#263f97]/35";

export const pageSizeChoices = [
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];
